import * as React from 'react'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import type {
  CalendarSubType,
  KnownCalendarSource,
  SourceStatus,
} from '../../typings'
import type { EventFilters, FilterKey } from '../../hooks/useEventFilters'
import type { UseActivityTypeFilterResult } from '../../hooks/useActivityTypeFilter'
import { SOURCE_LABELS, SUBTYPE_LABELS_PLURAL } from '../../displayMaps'
import { FilterChip } from './FilterChip'
import { ActivityTypeFilterMenu } from './ActivityTypeFilterMenu'
import { SourceUnavailableBadge } from './SourceUnavailableBadge'

type Props = {
  filters: EventFilters
  onToggle: (key: FilterKey) => void
  googleConnected: boolean
  sourceStatus: SourceStatus[]
  /** SH-2102: the expandable Activities chip, independent of the SH-1418 boolean filters above. */
  activityTypeFilter: UseActivityTypeFilterResult
  /** Retry for the Sales Center badge — a silent refetch, not a full reload. */
  onRetrySource?: () => void
}

// Plain SC events have no chip of their own — they ride along with the
// "Activities" toggle below.
const SUBTYPE_ORDER: CalendarSubType[] = [
  'next_action_date',
  'expected_close_date',
]

const KNOWN: ReadonlySet<string> = new Set<KnownCalendarSource>([
  'sales_center',
  'pestpac',
  'google',
])

export function FilterChipRow({
  filters,
  onToggle,
  googleConnected,
  sourceStatus,
  activityTypeFilter,
  onRetrySource,
}: Props) {
  const theme = useTheme()
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null)
  // A truncation-only status isn't a load failure: the data loaded, so it gets
  // the badge but not the chips' "unavailable" icon.
  const failed = new Set(
    sourceStatus
      .filter((s) => s.status === 'error' && !s.truncated)
      .map((s) => s.source),
  )
  const salesCenterStatus = sourceStatus.find(
    (s) => s.source === 'sales_center' && s.status === 'error',
  )
  // A failed source with no chip (e.g. Outlook) still has to be surfaced.
  const failedWithoutChip = [...failed].filter((source) => !KNOWN.has(source))

  const groupLabelSx = {
    fontFamily: theme.typography.fontFamily,
    fontSize: '12px',
    fontWeight: 600,
    color: theme.palette.text.secondary,
  }
  // Design's #9ca3af fails WCAG AA (2.54:1); grey[600] (#757575) passes (4.61:1).
  const salesCenterLabelSx = { ...groupLabelSx, color: theme.palette.grey[600] }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        minWidth: 0,
      }}
    >
      <Box component="span" sx={groupLabelSx}>
        Filters:
      </Box>
      <Box component="span" sx={salesCenterLabelSx}>
        Sales Center:
      </Box>

      <FilterChip
        label={activityTypeFilter.chipLabel}
        colorKey="activity_reminder"
        on={!activityTypeFilter.isAllOff}
        onToggle={() => {}}
        chevron
        onExpand={(anchorEl) => setMenuAnchor(anchorEl)}
        unavailable={failed.has('sales_center')}
      />
      <ActivityTypeFilterMenu
        activityTypes={activityTypeFilter.activityTypes}
        selectedIds={activityTypeFilter.selectedIds}
        onToggleType={activityTypeFilter.toggleType}
        onSelectAll={activityTypeFilter.selectAll}
        onClearAll={activityTypeFilter.clearAll}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      />
      {SUBTYPE_ORDER.map((subType) => (
        <FilterChip
          key={subType}
          label={SUBTYPE_LABELS_PLURAL[subType]}
          colorKey={subType}
          on={filters[subType]}
          onToggle={() => onToggle(subType)}
          unavailable={failed.has('sales_center')}
        />
      ))}

      {salesCenterStatus && (
        <SourceUnavailableBadge
          source="sales_center"
          label={SOURCE_LABELS.sales_center}
          message={salesCenterStatus.message}
          truncated={salesCenterStatus.truncated}
          onRetry={salesCenterStatus.truncated ? undefined : onRetrySource}
        />
      )}

      {/* Divider between the Sales Center group and the source group. */}
      <Box
        aria-hidden
        sx={{
          width: '1px',
          height: '16px',
          backgroundColor: theme.palette.grey[300],
        }}
      />

      <FilterChip
        label={SOURCE_LABELS.pestpac}
        colorKey="pestpac"
        on={filters.pestpac}
        onToggle={() => onToggle('pestpac')}
        unavailable={failed.has('pestpac')}
      />

      {/* Google chip removed from the DOM when the source is not connected. */}
      {googleConnected && (
        <FilterChip
          label={SOURCE_LABELS.google}
          colorKey="google"
          on={filters.google}
          onToggle={() => onToggle('google')}
          unavailable={failed.has('google')}
        />
      )}

      {failedWithoutChip.map((source) => (
        <SourceUnavailableBadge key={source} source={source} />
      ))}
    </Box>
  )
}
